import { FileText, Download, Camera, Image as ImageIcon, Loader2, Mail, MapPin, Phone } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { LoadingBackdrop } from '@components/loader'
import { AdasBox, AdasButton, AdasImage } from '@components/wrapper-components'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { getClientInvoice, getClientInvoiceMedia, getClientInvoicePdf, getClientInvoiceCOCPdf } from '../../../api/api'
import { useLoadingStore } from '../../../store'
import { currencyFormatter, phoneNumberFormat } from '../../../utils/utils'
import { REDIRECT_URLS } from '../../../constants'
import { Logger } from '../../../logger'
import { ItemRow } from '@components/custom-item'
import { useDeviceQueries } from '@hooks/useDeviceQueries'

export const ClientInvoiceSummaryPage = () => {
  const [invoiceData, setInvoiceData] = useState()
  const location = useLocation()
  const [isError, setIsError] = useState(false)
  const [pdfLoader, setPdfLoader] = useState(false)
  const [cocLoader, setCocLoader] = useState(false)

  const vehicleData = invoiceData ? invoiceData.vehicle : {}
  const searchParams = new URLSearchParams(location.search)
  const invoiceId = searchParams.get('invoiceId')
  const tempToken = searchParams.get('token')

  const textDisplay = { textAlign: 'center' }

  const history = useHistory()
  const setLoading = useLoadingStore((store) => store.setLoading)

  const redirectDocuments = () => {
    history.push(REDIRECT_URLS.CLIENT_INVOICE_SUMMARY_DOCUMENT, { ...invoiceData, id: invoiceId, tempToken })
  }

  const redirectPhotos = () => {
    history.push(REDIRECT_URLS.CLIENT_INVOICE_SUMMARY_PHOTO, { ...invoiceData, id: invoiceId, tempToken })
  }

  const packagesTotal = useMemo(() => {
    return invoiceData && invoiceData.calibrations.length > 0
      ? invoiceData.calibrations.reduce((total, current) => total + current.total_price, 0)
      : 0
  }, [invoiceData])

  const servicesTotal = useMemo(() => {
    return invoiceData && invoiceData.services.length > 0
      ? invoiceData.services.reduce((total, current) => total + current.total_price, 0)
      : 0
  }, [invoiceData])
  const partsTotal = useMemo(() => {
    return invoiceData && invoiceData.parts.length > 0 ? invoiceData.parts.reduce((total, current) => total + current.total_price, 0) : 0
  }, [invoiceData])
  const laborTotal = useMemo(() => {
    return invoiceData && invoiceData.labours.length > 0
      ? invoiceData.labours.reduce((total, current) => total + current.total_price, 0)
      : 0
  }, [invoiceData])
  // The backend says whether this link's version leaves the client rebate off (the retail
  // invoice does — its custom discounts stay, CAR-3893); the page drops that row and totals
  // accordingly so it matches the PDF the link hands out.
  const rebateHidden = invoiceData?.rebateHidden === true

  const customItems = useMemo(() => {
    const items = invoiceData?.customItems ?? []
    return rebateHidden ? items.filter((item) => !item.is_client_rebate_applied) : items
  }, [invoiceData, rebateHidden])

  const customItemTotal = useMemo(() => {
    return customItems.reduce((total_price, current) => {
      if (current.type === 'DISCOUNT') {
        return total_price - current.total_price
      } else {
        return total_price + current.total_price
      }
    }, 0)
  }, [customItems])

  const totalCost = packagesTotal + servicesTotal + customItemTotal + partsTotal + laborTotal

  useEffect(() => {
    const params = {
      invoiceId,
      token: tempToken
    }
    const invoiceResponse = getClientInvoice(params)
    invoiceResponse
      .then((response) => {
        if (response.status === 200) {
          const data = response.data
          data.invoiceDto.safetySystemsItems = response.data.safetySystemsItems
          data.invoiceDto.calibrations = response.data.qwiCalibrations
          data.invoiceDto.services = response.data.qwiServices
          data.invoiceDto.parts = response.data.qwiParts
          data.invoiceDto.labours = response.data.qwiLabours
          data.invoiceDto.customItems = response.data.qwiCustomItems
          // Which invoice version this link represents; the backend binds it to the
          // token, so a QR scanned off a cost invoice lands here as COST.
          data.invoiceDto.pdfVariant = response.data.pdfVariant
          data.invoiceDto.rebateHidden = response.data.rebateHidden
          setInvoiceData(data.invoiceDto)
        }
      })
      .catch(() => {
        setIsError(true)
      })
  }, [invoiceId, tempToken, history])

  const CalibrationCenterInfo = (props) => {
    const { calibrationCenter } = props
    return (
      <AdasBox className='flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#D1D5D8] bg-white px-5 py-4'>
        {/* Left: logo slot — renders the calibration center logo when present, otherwise leaves blank space. */}
        <AdasBox className='h-[90px] w-[140px] flex-none overflow-hidden rounded-lg'>
          {calibrationCenter.image && <AdasImage src={calibrationCenter.image} alt='' className='h-full w-full object-contain' />}
        </AdasBox>

        {/* Right: ADAS INTELLIGENCE eyebrow + center name + contact, right-aligned */}
        <AdasBox className='text-right'>
          <div className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>ADAS Intelligence</div>
          <div className='mt-0.5 text-[18px] font-bold text-[#0A1628]'>{calibrationCenter.name}</div>
          <AdasBox className='mt-2 space-y-1 text-[13px] text-[#2C3D4D]'>
            {calibrationCenter.shop_address && (
              <div className='flex items-center justify-end gap-2'>
                <span>
                  {calibrationCenter.shop_address}
                  {calibrationCenter.shop_city ? `, ${calibrationCenter.shop_city}` : ''}
                  {calibrationCenter.shop_state ? `, ${calibrationCenter.shop_state}` : ''}
                </span>
                <MapPin className='h-3.5 w-3.5 text-[#475569]' />
              </div>
            )}
            {calibrationCenter.phone && (
              <div className='flex items-center justify-end gap-2'>
                <span>{phoneNumberFormat(calibrationCenter.phone)}</span>
                <Phone className='h-3.5 w-3.5 text-[#475569]' />
              </div>
            )}
            {calibrationCenter.email && (
              <div className='flex items-center justify-end gap-2'>
                <a href={`mailto:${calibrationCenter.email}`} className='hover:underline'>
                  {calibrationCenter.email}
                </a>
                <Mail className='h-3.5 w-3.5 text-[#475569]' />
              </div>
            )}
          </AdasBox>
        </AdasBox>
      </AdasBox>
    )
  }

  const ErrorMessage = () => {
    return (
      <AdasBox className='overflow-auto text-center grow bg-[#E1E9F0] text-[30px] h-[100dvh]'>
        <AdasBox className='mt-[100px] text-red-600'>
          Invoice link has expired. Please request an updated invoice email from your calibration center.
        </AdasBox>
      </AdasBox>
    )
  }
  const displayPDFContent = (pdfContent, fileName) => {
    const fileValue = new Blob([pdfContent], { type: 'application/pdf' }, fileName)
    fileValue.id = invoiceId
    const fileURL = URL.createObjectURL(fileValue)
    const pdfWindow = window.open()
    if (pdfWindow === null) {
      alert('Pop-up was blocked by your browser! Please allow pop-ups for caradasplus.com to view Invoice.')
    } else {
      pdfWindow.location.href = fileURL
    }
  }

  const viewPdf = async () => {
    try {
      setPdfLoader(true)
      const invoicePdfResponse = await getClientInvoicePdf({
        invoiceId: invoiceId,
        token: tempToken
      })
      if (invoicePdfResponse.status === 200) {
        displayPDFContent(invoicePdfResponse.data, `invoice_${invoiceData.reference_number}.pdf`)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { invoiceId, path: 'ClientInvoiceSummaryPage/viewPdf' } })
    } finally {
      setPdfLoader(false)
    }
  }

  const viewCOCPdf = async () => {
    try {
      setCocLoader(true)
      const invoicePdfResponse = await getClientInvoiceCOCPdf({
        invoiceId: invoiceId,
        token: tempToken
      })
      setCocLoader(false)
      if (invoicePdfResponse.status === 200) {
        displayPDFContent(invoicePdfResponse.data, `certificate_${invoiceId}.pdf`)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { invoiceId, path: 'InvoiceSummaryPage/viewCOC' } })
    } finally {
      setCocLoader(false)
    }
  }

  const downloadMediaHandler = async () => {
    try {
      setLoading(true)
      const invoiceMediaResponse = await getClientInvoiceMedia({
        invoiceId: invoiceId,
        token: tempToken
      })
      setLoading(false)
      if (invoiceMediaResponse.status === 200) {
        const fileValue = new Blob([invoiceMediaResponse.data], { type: 'application/zip' }, `invoice_${invoiceData.reference_number}.zip`)
        fileValue.id = invoiceId
        const fileURL = URL.createObjectURL(fileValue)
        const link = document.createElement('a')
        link.href = fileURL
        link.setAttribute('download', `invoice_${invoiceData.reference_number}.zip`)
        document.body.appendChild(link)
        link.click()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { invoiceId, path: 'InvoiceSummaryPage/download' } })
    } finally {
      setLoading(false)
    }
  }

  return !isError ? (
    invoiceData && (
      <div className='mx-auto max-w-[1400px] px-3 py-6 min-[435px]:px-6'>
        <LoadingBackdrop />

        {/* Calibration center card */}
        <CalibrationCenterInfo calibrationCenter={invoiceData.calibrationCenter} />

        {/* Invoice details card — vehicle placeholder + title/VIN + meta grid (left) · Total Cost chip + VIEW buttons (right). */}
        <AdasBox className='mt-4 flex flex-wrap items-stretch justify-between gap-6 rounded-xl border border-[#D1D5D8] bg-white p-4'>
          {/* Left column: vehicle row + meta grid */}
          <AdasBox className='flex min-w-0 flex-1 flex-col gap-4'>
            <AdasBox className='flex items-start gap-4'>
              <AdasBox className='flex h-[90px] w-[140px] flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-[#D1D5D8] bg-[#F8FAFC] text-center text-[12px] text-[#6B7280]'>
                {vehicleData?.image_url ? (
                  <AdasImage src={vehicleData.image_url} alt='Vehicle' className='h-full w-full object-contain' />
                ) : (
                  <div className='flex flex-col items-center gap-1'>
                    <ImageIcon className='h-4 w-4 text-[#9CA3AF]' />
                    <span className='text-[12px] text-[#0A1628]'>Vehicle</span>
                    <span className='text-[11px] text-[#6B7280]'>or browse files</span>
                  </div>
                )}
              </AdasBox>
              <AdasBox className='min-w-0 flex-1 pt-1'>
                <div className='text-[18px] font-bold leading-tight text-[#0A1628]'>
                  {vehicleData
                    ? [vehicleData.year, vehicleData.make, vehicleData.model, vehicleData.trim].filter(Boolean).join(' ') || 'Vehicle'
                    : 'Vehicle'}
                </div>
                {vehicleData?.vin && <div className='mt-1 font-mono text-[13px] tracking-wider text-[#2C3D4D]'>VIN: {vehicleData.vin}</div>}
                {invoiceData.repair_order_num && (
                  <div className='mt-1 font-mono text-[12px] tracking-wider text-[#6B7280]'>{invoiceData.repair_order_num}</div>
                )}
              </AdasBox>
            </AdasBox>

            {/* Meta grid: INVOICE # / RO # / SERVICED BY / DATE */}
            <AdasBox className='grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] sm:grid-cols-4'>
              <div>
                <div className='text-[10px] font-bold uppercase tracking-wide text-[#1F6FEB]'>Invoice #</div>
                <div className='mt-1 font-mono text-[14px] font-semibold text-[#0A1628]'>#{invoiceData.reference_number}</div>
              </div>
              {invoiceData.repair_order_num && (
                <div>
                  <div className='text-[10px] font-bold uppercase tracking-wide text-[#1F6FEB]'>RO #</div>
                  <div className='mt-1 font-mono text-[14px] text-[#0A1628]'>{invoiceData.repair_order_num}</div>
                </div>
              )}
              {invoiceData.assigned_to?.name && (
                <div>
                  <div className='text-[10px] font-bold uppercase tracking-wide text-[#1F6FEB]'>Serviced By</div>
                  <div className='mt-1 text-[14px] text-[#0A1628]'>{invoiceData.assigned_to.name}</div>
                </div>
              )}
              {invoiceData.created && (
                <div>
                  <div className='text-[10px] font-bold uppercase tracking-wide text-[#1F6FEB]'>Date</div>
                  <div className='mt-1 font-mono text-[14px] text-[#0A1628]'>{moment(invoiceData.created).format('MM/DD/YYYY')}</div>
                </div>
              )}
            </AdasBox>
          </AdasBox>

          {/* Right column: VIEW buttons (stacked) */}
          <AdasBox className='flex w-full flex-col gap-2 sm:w-[280px]'>
            <AdasButton
              onClick={viewPdf}
              startIcon={pdfLoader ? <Loader2 className='w-4 h-4 animate-spin' /> : <Download className='w-4 h-4' />}
              variant='contained'
              className='!justify-center !rounded-lg !bg-[#0A1628] hover:!bg-[#1F2D3D]'
            >
              VIEW INVOICE
            </AdasButton>
            <AdasButton
              onClick={viewCOCPdf}
              startIcon={cocLoader ? <Loader2 className='w-4 h-4 animate-spin' /> : <Download className='w-4 h-4' />}
              variant='contained'
              className='!justify-center !rounded-lg !bg-[#0A1628] hover:!bg-[#1F2D3D]'
            >
              VIEW CERTIFICATE
            </AdasButton>
          </AdasBox>
        </AdasBox>

        {/* Items table — navy header + flat rows + total cost footer */}
        <AdasBox className='mt-4 overflow-hidden rounded-xl border border-[#D1D5D8] bg-white'>
          <div className='grid grid-cols-[1fr_80px_120px_140px] items-center gap-2 bg-gradient-to-r from-[#0A1628] to-[#1F2D3D] px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-white'>
            <div>Name</div>
            <div className='text-center'>Qty</div>
            <div className='text-right'>Unit Cost</div>
            <div className='text-right'>Total Amount</div>
          </div>
          <div className='px-2'>
            <ItemList itemList={invoiceData.calibrations} disabled={true} type={'calibration'} />
            <ItemList itemList={invoiceData.services} disabled={true} type={'services'} />
            <ItemList itemList={invoiceData.parts} disabled={true} type={'parts'} />
            <ItemList itemList={invoiceData.labours} disabled={true} type={'labours'} />
            <ItemList itemList={customItems} disabled={true} type={'custom'} />
          </div>
          <div className='flex items-center justify-end gap-6 border-t border-[#EBEDF0] px-4 py-3 text-[13px]'>
            <span className='text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]'>Total Cost</span>
            <span className='text-[18px] font-bold text-[#0A1628]'>{currencyFormatter(totalCost)}</span>
          </div>
        </AdasBox>

        {/* Sub-page nav strip — Photos / Documents (matches Invoice Summary) + Download All Media */}
        <AdasBox className='mt-4 flex flex-nowrap items-center gap-2 overflow-x-auto'>
          <button
            type='button'
            onClick={redirectPhotos}
            className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
          >
            <Camera className='h-4 w-4 shrink-0' />
            Photos
          </button>
          <button
            type='button'
            onClick={redirectDocuments}
            className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[#D1D5D8] bg-white px-3 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
          >
            <FileText className='h-4 w-4 shrink-0' />
            Documents
          </button>
          <button
            type='button'
            onClick={downloadMediaHandler}
            className='inline-flex flex-1 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[#0A1628] px-3 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#1F2D3D]'
          >
            <Download className='h-4 w-4 shrink-0' />
            Download All Media
          </button>
        </AdasBox>
      </div>
    )
  ) : (
    <ErrorMessage />
  )
}
export const ItemList = ({ itemList, isCustomItem = true, disabled, onClickEdit, type }) => {
  const { isMobile } = useDeviceQueries()

  return (
    <AdasBox>
      {itemList?.length > 0 &&
        itemList.map((item, index) => (
          <ItemRow
            key={index}
            item={item}
            isCustomItem={isCustomItem}
            disabled={disabled}
            onClickEdit={onClickEdit}
            isMobile={isMobile}
            type={type}
            isCustomInvoice={true} // This prop is used to differentiate between custom and non-custom items
          />
        ))}
    </AdasBox>
  )
}
